import React, { useState, useEffect, useCallback } from 'react';
import { db } from '../firebase';
import {
  doc, getDoc, updateDoc, addDoc, collection, query, where, getDocs, serverTimestamp
} from 'firebase/firestore';

function AdminPanel({ user, userDocId, isAdmin, onRoleChange }) {
  const [pendingAssociations, setPendingAssociations] = useState([]);
  const [adminLoading, setAdminLoading] = useState(false);
  const [msg, setMsg] = useState("");

  // 승인 대기 중인 협회 목록 로드
  const fetchPendingAssociations = useCallback(async () => {
    if (!isAdmin) return;
    
    try {
      setAdminLoading(true);
      const q = query(
        collection(db, "users"),
        where("userType", "==", "association"),
        where("isApproved", "==", false)
      );
      const snap = await getDocs(q);
      
      const associations = snap.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      setPendingAssociations(associations);
    } catch (error) {
      console.error("승인 대기 협회 로드 실패:", error);
      setMsg("승인 대기 협회 목록을 불러올 수 없습니다.");
    } finally {
      setAdminLoading(false);
    }
  }, [isAdmin]);

  // 협회 승인/거부 처리
  const handleAssociationApproval = useCallback(async (associationId, approved) => {
    if (!isAdmin) return;
    
    // 승인/거부 확인 알림창
    const action = approved ? "승인" : "거부";
    const confirmed = window.confirm(
      `정말로 이 협회를 ${action}하시겠습니까?\n\n` +
      `이 작업은 되돌릴 수 없습니다.`
    );
    
    if (!confirmed) return;
    
    try {
      setAdminLoading(true);
      
      // 협회 정보 먼저 가져오기
      const userRef = doc(db, "users", associationId);
      const userSnap = await getDoc(userRef);
      
      if (!userSnap.exists()) {
        setMsg("협회 정보를 찾을 수 없습니다.");
        return;
      }
      
      const associationData = userSnap.data();
      
      // 승인/거부 처리
      await updateDoc(userRef, {
        isApproved: approved,
        approvedAt: serverTimestamp(),
        approvedBy: user.id
      });
      
      // 승인된 경우 알림 생성
      if (approved) {
        await addDoc(collection(db, 'notifications'), {
          type: 'association_approved',
          to: associationData.id,
          message: `🎉 축하합니다! ${associationData.associationName} 협회가 승인되었습니다. 이제 로그인하여 서비스를 이용하실 수 있습니다.`,
          status: 'unread',
          createdAt: serverTimestamp()
        });
      } else {
        // 거부된 경우 알림 생성
        await addDoc(collection(db, 'notifications'), {
          type: 'association_rejected',
          to: associationData.id,
          message: `❌ ${associationData.associationName} 협회 신청이 거부되었습니다. 자세한 사항은 관리자에게 문의해주세요.`,
          status: 'unread',
          createdAt: serverTimestamp()
        });
      }
      
      // 성공 메시지 표시
      const successMsg = approved 
        ? `✅ ${associationData.associationName} 협회가 성공적으로 승인되었습니다!`
        : `❌ ${associationData.associationName} 협회가 거부되었습니다.`;
      
      setMsg(successMsg);
      
      // 3초 후 메시지 지우기
      setTimeout(() => {
        setMsg("");
      }, 3000);
      
      // 목록 새로고침
      await fetchPendingAssociations();
      
    } catch (error) {
      console.error("협회 승인 처리 실패:", error);
      setMsg("승인 처리 중 오류가 발생했습니다.");
    } finally {
      setAdminLoading(false);
    }
  }, [isAdmin, user?.id, fetchPendingAssociations]);

  // 사용자 등급 변경
  const handleRoleChange = useCallback(async (userId, newRole) => {
    if (!isAdmin) return;
    
    // 현재 사용자 정보 가져오기
    const userRef = doc(db, "users", userId);
    const userSnap = await getDoc(userRef);
    
    if (!userSnap.exists()) {
      setMsg("사용자 정보를 찾을 수 없습니다.");
      return;
    }
    
    const currentUserData = userSnap.data();
    const currentRole = currentUserData.role || 'user';
    
    // 이미 같은 등급인 경우 처리하지 않음
    if (currentRole === newRole) {
      setMsg("이미 같은 등급입니다.");
      return;
    }
    
    // 등급 변경 확인 알림창
    const roleText = newRole === 'admin' ? '관리자' : '일반 사용자';
    const confirmed = window.confirm(
      `정말로 이 사용자의 등급을 ${roleText}로 변경하시겠습니까?\n\n` +
      `사용자: ${currentUserData.nickname || currentUserData.id}\n` +
      `현재 등급: ${currentRole === 'admin' ? '관리자' : '일반 사용자'}\n` +
      `변경할 등급: ${roleText}`
    );
    
    if (!confirmed) return;
    
    try {
      setAdminLoading(true);
      
      await updateDoc(userRef, {
        role: newRole,
        roleChangedAt: serverTimestamp(),
        roleChangedBy: user.id
      });
      
      // 등급 변경 알림 생성
      await addDoc(collection(db, 'notifications'), {
        type: 'role_changed',
        to: userId,
        message: `🔔 계정 등급이 ${roleText}로 변경되었습니다.`,
        status: 'unread',
        createdAt: serverTimestamp()
      });
      
      const successMsg = `✅ ${currentUserData.nickname || currentUserData.id} 사용자의 등급이 ${roleText}로 변경되었습니다!`;
      setMsg(successMsg);
      
      // 3초 후 메시지 지우기
      setTimeout(() => {
        setMsg("");
      }, 3000);
      
      // 부모 컴포넌트에 변경 알림
      if (onRoleChange) {
        onRoleChange();
      }
    } catch (error) {
      console.error("등급 변경 실패:", error);
      setMsg("등급 변경 중 오류가 발생했습니다.");
    } finally {
      setAdminLoading(false);
    }
  }, [isAdmin, user?.id, onRoleChange]);

  // 초기 로드
  useEffect(() => {
    if (isAdmin) {
      fetchPendingAssociations();
    }
  }, [isAdmin, fetchPendingAssociations]);

  if (!isAdmin) {
    return null;
  }

  return (
    <div style={{
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))',
      gap: '24px'
    }}>
      {/* 관리자 통계 카드 */}
      <div style={{
        background: '#fff',
        borderRadius: '16px',
        padding: '24px',
        boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
        border: '1px solid #e5e7eb'
      }}>
        <h3 style={{
          fontSize: '20px',
          fontWeight: 'bold',
          marginBottom: '24px',
          color: '#1f2937'
        }}>
          👑 관리자 통계
        </h3>
        
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(2, 1fr)',
          gap: '16px'
        }}>
          <div style={{
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            padding: '20px',
            borderRadius: '12px',
            color: '#fff',
            textAlign: 'center'
          }}>
            <div style={{
              fontSize: '32px',
              fontWeight: 'bold',
              marginBottom: '8px'
            }}>
              {pendingAssociations.length}
            </div>
            <div style={{
              fontSize: '14px',
              opacity: 0.9
            }}>
              승인 대기
            </div>
          </div>
          <div style={{
            background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
            padding: '20px',
            borderRadius: '12px',
            color: '#fff',
            textAlign: 'center'
          }}>
            <div style={{
              fontSize: '32px',
              fontWeight: 'bold',
              marginBottom: '8px'
            }}>
              {user?.role === 'admin' ? '관리자' : '일반'}
            </div>
            <div style={{
              fontSize: '14px',
              opacity: 0.9
            }}>
              현재 등급
            </div>
          </div>
        </div>
      </div>

      {/* 사용자 등급 변경 카드 */}
      <div style={{
        background: '#fff',
        borderRadius: '16px',
        padding: '24px',
        boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
        border: '1px solid #e5e7eb'
      }}>
        <h3 style={{
          fontSize: '20px',
          fontWeight: 'bold',
          marginBottom: '24px',
          color: '#1f2937'
        }}>
          🔧 사용자 등급 변경
        </h3>
        
        <div style={{
          marginBottom: '16px'
        }}>
          <label style={{
            display: 'block',
            fontSize: '14px',
            fontWeight: '600',
            color: '#374151',
            marginBottom: '8px'
          }}>
            현재 사용자: {user?.nickname || user?.id}
          </label>
          <select
            value={user?.role || 'user'}
            onChange={(e) => handleRoleChange(userDocId, e.target.value)}
            disabled={adminLoading}
            style={{
              width: '100%',
              padding: '12px',
              borderRadius: '8px',
              border: '1px solid #d1d5db',
              fontSize: '14px',
              background: adminLoading ? '#f3f4f6' : '#fff'
            }}
          >
            <option value="user">일반 사용자</option>
            <option value="admin">관리자</option>
          </select>
        </div>
        
        {adminLoading && (
          <div style={{
            textAlign: 'center',
            padding: '12px',
            color: '#6b7280',
            fontSize: '14px'
          }}>
            처리 중...
          </div>
        )}
      </div>

      {/* 협회 승인 대기 카드 */}
      <div style={{
        background: '#fff',
        borderRadius: '16px',
        padding: '24px',
        boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
        border: '1px solid #e5e7eb',
        gridColumn: '1 / -1'
      }}>
        <h3 style={{
          fontSize: '20px',
          fontWeight: 'bold',
          marginBottom: '24px',
          color: '#1f2937'
        }}>
          🏢 협회 승인 대기 ({pendingAssociations.length}개)
        </h3>
        
        {adminLoading ? (
          <div style={{
            textAlign: 'center',
            padding: '40px',
            color: '#6b7280'
          }}>
            로딩 중...
          </div>
        ) : pendingAssociations.length > 0 ? (
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))',
            gap: '16px'
          }}>
            {pendingAssociations.map((association) => (
              <div
                key={association.id}
                style={{
                  padding: '20px',
                  background: '#f8fafc',
                  borderRadius: '12px',
                  border: '1px solid #e2e8f0',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
                }}
              >
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'flex-start',
                  marginBottom: '12px'
                }}>
                  <div style={{ flex: 1 }}>
                    <div style={{
                      fontWeight: 'bold',
                      fontSize: '18px',
                      color: '#1f2937',
                      marginBottom: '8px'
                    }}>
                      {association.associationName}
                    </div>
                    <div style={{
                      fontSize: '14px',
                      color: '#6b7280',
                      marginBottom: '4px'
                    }}>
                      대표자: {association.representativeName}
                    </div>
                    <div style={{
                      fontSize: '12px',
                      color: '#9ca3af',
                      marginBottom: '2px'
                    }}>
                      사업자번호: {association.businessNumber}
                    </div>
                    <div style={{
                      fontSize: '12px',
                      color: '#9ca3af',
                      marginBottom: '2px'
                    }}>
                      주소: {association.associationAddress}
                    </div>
                    <div style={{
                      fontSize: '12px',
                      color: '#9ca3af'
                    }}>
                      운영종목: {association.sports?.join(', ') || '없음'}
                    </div>
                  </div>
                  <div style={{
                    display: 'flex',
                    gap: '8px'
                  }}>
                    <button
                      onClick={() => handleAssociationApproval(association.id, true)}
                      disabled={adminLoading}
                      style={{
                        background: '#10b981',
                        color: '#fff',
                        border: 'none',
                        borderRadius: '8px',
                        padding: '10px 16px',
                        fontSize: '14px',
                        fontWeight: '600',
                        cursor: adminLoading ? 'not-allowed' : 'pointer',
                        opacity: adminLoading ? 0.6 : 1,
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px'
                      }}
                      title="협회 승인"
                    >
                      ✅ 승인
                    </button>
                    <button
                      onClick={() => handleAssociationApproval(association.id, false)}
                      disabled={adminLoading}
                      style={{
                        background: '#ef4444',
                        color: '#fff',
                        border: 'none',
                        borderRadius: '8px',
                        padding: '10px 16px',
                        fontSize: '14px',
                        fontWeight: '600',
                        cursor: adminLoading ? 'not-allowed' : 'pointer',
                        opacity: adminLoading ? 0.6 : 1,
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px'
                      }}
                      title="협회 거부"
                    >
                      ❌ 거부
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div style={{
            textAlign: 'center',
            padding: '40px',
            color: '#6b7280'
          }}>
            <div style={{ fontSize: '48px', marginBottom: '16px' }}>🎉</div>
            <div style={{ fontSize: '18px', fontWeight: '600', marginBottom: '8px' }}>
              승인 대기 중인 협회가 없습니다
            </div>
            <div style={{ fontSize: '14px' }}>
              모든 협회가 처리되었습니다!
            </div>
          </div>
        )}
      </div>

      {/* 메시지 표시 */}
      {msg && (
        <div style={{
          position: 'fixed',
          top: '20px',
          left: '50%',
          transform: 'translateX(-50%)',
          background: msg.includes('성공') ? '#10b981' : '#ef4444',
          color: '#fff',
          padding: '12px 24px',
          borderRadius: '8px',
          fontSize: '14px',
          fontWeight: '600',
          zIndex: 1000,
          boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
        }}>
          {msg}
        </div>
      )}
    </div>
  );
}

export default AdminPanel; 